import type { internal } from '@croissants/contracts';
import {
  type CounterReadiness,
  counterTakesOrders,
  type GuestChoiceFacts,
  reassignConfinedToHouse,
} from '@croissants/contracts/projections';
import type { PillState } from '@croissants/ui';

/**
 * The Health status board: the pre-service readiness check (PRD OPS-010), the
 * tablet-heartbeat alert (PRD OPS-011) and the delivery provider (PRD RTE-022),
 * as pure derivations.
 *
 * The screen is called Health status because it answers one question - can we
 * serve this morning - from three sources. The 05:45 check keeps its own name
 * wherever it is the thing being run (`internal.getReadiness`, the NTF-006 alert,
 * OPS-010): it is one input to the board, not the board.
 *
 * The check produces one green/red verdict, but an operator at 05:46 needs to
 * know *which* condition is red and what to do about it, so each condition maps
 * to its own catalogue keys - a title and a remedy - and the board is ordered
 * with the failures first. A green board must never be able to hide a red row.
 */

export type ReadinessConditionKey = internal.ReadinessCheck['conditions'][number]['key'];

export const READINESS_CONDITION_ORDER: readonly ReadinessConditionKey[] = [
  'ALL_ORDERS_ALLOCATED',
  'ALL_ORDERS_ACCEPTED',
  'TABLET_HEARTBEATS',
  'PROVIDER_API_REACHABLE',
  'WEBHOOK_ENDPOINTS_REACHABLE',
  'DEAD_LETTER_EMPTY',
  'NO_PENDING_CAPTURES',
];

export interface ReadinessConditionView {
  readonly key: ReadinessConditionKey;
  readonly passed: boolean;
  readonly detail: string;
  readonly titleKey: string;
  readonly remedyKey: string;
}

export function conditionCatalogueKeys(): readonly string[] {
  const keys: string[] = [];
  for (const key of READINESS_CONDITION_ORDER) {
    keys.push(`health.condition.${key}.title`, `health.condition.${key}.remedy`);
  }
  return keys;
}

/**
 * Failures first, then the platform's own order. A condition the server did not
 * report is surfaced as failed rather than omitted: a check that silently stops
 * running is indistinguishable from one that passes, and that is the failure
 * mode this board exists to prevent.
 */
export function conditionViews(
  check: internal.ReadinessCheck | undefined,
): ReadinessConditionView[] {
  const reported = new Map(check?.conditions.map((c) => [c.key, c]) ?? []);
  const views = READINESS_CONDITION_ORDER.map((key): ReadinessConditionView => {
    const found = reported.get(key);
    return {
      key,
      passed: found?.passed ?? false,
      detail: found?.detail ?? '',
      titleKey: `health.condition.${key}.title`,
      remedyKey: `health.condition.${key}.remedy`,
    };
  });
  return views.sort((a, b) => Number(a.passed) - Number(b.passed));
}

/**
 * A verdict is green only when every condition passed. The server's own `green`
 * flag is honoured but never allowed to overrule a red condition - two sources
 * that can disagree must fail closed.
 */
export function isGreen(check: internal.ReadinessCheck | undefined): boolean {
  if (check === undefined) return false;
  return check.green && check.conditions.every((condition) => condition.passed);
}

/** How many of the conditions the board knows about are currently passing. */
export function passingCount(check: internal.ReadinessCheck | undefined): number {
  return conditionViews(check).filter((view) => view.passed).length;
}

// ---------------------------------------------------------------------------
// Bakery tablet heartbeats (PRD BAK-020, OPS-011)
// ---------------------------------------------------------------------------

/**
 * Structurally the `bakeryReadiness` row of the dashboard response - one
 * SHOP, never the house that owns it. Declared as an interface rather than
 * indexed out of the contract so the pure helpers below stay usable from a
 * test fixture that is not a whole API response.
 */
export interface BakeryReadinessLike {
  readonly locationId: string;
  /** The house that owns the shop - what ALC-010's confinement is asked over. */
  readonly houseId: string;
  /** The shop's own name (BAK-028). */
  readonly name: string;
  readonly houseName: string;
  readonly storefrontMode: 'A' | 'B';
  readonly active: boolean;
  /** OPS-035 - the counter's one word, decided by the server's projection. */
  readonly readiness: CounterReadiness;
  readonly tabletLastSeenAt: string | null;
  readonly tabletHealthy: boolean;
  readonly deviceCount: number;
  /** The counter's own board, lane for lane (BAK-002). */
  readonly board: {
    readonly NEW: number;
    readonly PREPARE_LATER: number;
    readonly PREPARE_NOW: number;
    readonly READY: number;
    readonly COLLECTED: number;
    readonly ISSUES: number;
  };
  readonly amendedOrders: number;
  readonly earliestReadyLocalTime: string;
  readonly paused: boolean;
}

/**
 * How much work this counter is carrying right now - every order the baker can
 * still do something about. Collected is deliberately out of it: those bags
 * have left the shop, and by 11:00 they are the largest number on the row while
 * being the one nobody has to act on. Issues is in, because somebody does.
 */
export function openOrderLoad(row: BakeryReadinessLike): number {
  return row.board.NEW + row.board.PREPARE_LATER + row.board.PREPARE_NOW + row.board.READY;
}

/** Shop availability and equipment are reported separately on the row. */
export type BakeryHealth = 'RED' | 'AMBER' | 'GREY' | 'GREEN';

/**
 * The delivery provider's verdict: the three of `BakeryHealth` a provider can
 * be (GREY is a counter that cannot be given an order, and a provider is not a
 * counter), which are the shared pill list's `healthVerdict` (#2004).
 */
export type ProviderHealth = PillState<'healthVerdict'>;

export function bakeryHealth(row: BakeryReadinessLike): BakeryHealth {
  if (row.readiness === 'UNREACHABLE') return 'RED';
  if ((row.paused || row.readiness === 'NOT_EQUIPPED') && openOrderLoad(row) > 0) return 'RED';
  if (row.paused || !counterTakesOrders(row.readiness)) return 'GREY';
  if (row.board.NEW > 0) return 'AMBER';
  return 'GREEN';
}

export function bakeryVerdictKey(row: BakeryReadinessLike): string {
  if (row.paused) return 'health.bakeries.ordersPaused';
  return counterTakesOrders(row.readiness)
    ? 'health.bakeries.takesOrders'
    : 'health.bakeries.noOrders';
}

/**
 * The shops a readiness section lists (OPS-003, founder decision 2026-08-24).
 *
 * A suspended counter - switched off itself, or dark because its house is -
 * wins no allocation, has no morning and cannot be ready for one, so listing
 * it under readiness is a permanent grey row an operator learns to scroll
 * past. It stays in the response because the board's bakery filter is built
 * from the same array and its orders are still lookup-able.
 */
export function readinessShops<T extends BakeryReadinessLike>(
  rows: readonly T[],
  mode: 'A' | 'B',
): T[] {
  return sortBakeries(rows.filter((row) => row.active && row.storefrontMode === mode));
}

/**
 * How a SHOP is named in a bakery picker - the dashboard's filter, the orders
 * filter, the reassign dialog.
 *
 * Three pickers named the HOUSE, which meant a chain offered the same option
 * three times over and picking one of them filtered to a location the operator
 * had no way to identify. The shop leads; the house is appended only when it
 * adds something, exactly as the readiness row does.
 */
export function bakeryPickerLabel(row: BakeryReadinessLike): string {
  return row.houseName === row.name ? row.name : `${row.houseName} - ${row.name}`;
}

/** The order facts that decide which shops may receive it (ALC-002, ALC-010, OPS-013). */
export interface ReassignTargetOf extends GuestChoiceFacts {
  /** Where it is now - null while it is unallocated. */
  readonly bakeryLocationId: string | null;
}

/**
 * The shops an order can be REASSIGNED to, which is the set the server will
 * actually accept (`makeReassignOrderBakery`, packages/services) and no wider.
 *
 * The picker was built off the readiness array with `active` as its only
 * filter, so on the demo world a mode A order was offered twenty-four
 * destinations of which twenty-three answered `409 target_mode_incompatible` -
 * the same enabled-is-not-legal defect F-34 closed on the control itself,
 * reopened one field to its left. Four rules, each one a refusal the use-case
 * spells out by name:
 *
 *   - a suspended shop wins no allocation and bakes nothing (OPS-003);
 *   - a house selling in the OTHER storefront mode is refused outright
 *     (OPS-013, ALC-002) - mode is a property of the house, not of the order
 *     being moved, so the two must match;
 *   - a guest-chosen mode B order is confined to the house the guest chose
 *     (ALC-010, `reassignConfinedToHouse`), so only that house's other shops
 *     are offered - and none at all if this screen cannot tell which house
 *     that is, which is the safe way to be wrong about it;
 *   - the shop the order is already at is not a destination. The server would
 *     take it and write an audit row for a move that moved nothing.
 *
 * Distance is deliberately NOT one of them: there is no per-shop service
 * radius (founder, 2026-09-24), and the operator names the shop by hand.
 */
export function reassignTargets<T extends BakeryReadinessLike>(
  rows: readonly T[],
  order: ReassignTargetOf,
): { value: string; label: string }[] {
  const confined = reassignConfinedToHouse(order);
  const chosenHouseId =
    rows.find((row) => row.locationId === order.bakeryLocationId)?.houseId ?? null;
  return rows
    .filter(
      (row) =>
        row.active &&
        row.storefrontMode === order.storefrontMode &&
        row.locationId !== order.bakeryLocationId &&
        (!confined || (chosenHouseId !== null && row.houseId === chosenHouseId)),
    )
    .map((row) => ({ value: row.locationId, label: bakeryPickerLabel(row) }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * GREY sits between the morning's problems and the shops that are fine: a
 * counter that has stopped selling and owes nobody a bag is not this morning's
 * emergency, and it is not a row to bury under thirty green ones either - it is
 * the row an operator scrolls to when the phones are quiet.
 */
const HEALTH_RANK: Readonly<Record<BakeryHealth, number>> = {
  RED: 0,
  AMBER: 1,
  GREY: 2,
  GREEN: 3,
};

/** Worst first, then most orders at stake, then by name so the list is stable. */
export function sortBakeries<T extends BakeryReadinessLike>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => {
    const healthDelta = HEALTH_RANK[bakeryHealth(a)] - HEALTH_RANK[bakeryHealth(b)];
    if (healthDelta !== 0) return healthDelta;
    const loadDelta = openOrderLoad(b) - openOrderLoad(a);
    if (loadDelta !== 0) return loadDelta;
    return a.name.localeCompare(b.name);
  });
}

// ---------------------------------------------------------------------------
// Delivery provider (PRD RTE-022, RTE-023)
// ---------------------------------------------------------------------------

/**
 * Structurally the `getProviderStatus` response, for the same reason
 * `BakeryReadinessLike` is an interface.
 */
export interface ProviderStatusLike {
  /** Null when no delivery provider is set (RTE-023): nothing can book. */
  readonly active?: string | null;
  readonly declarations: readonly {
    readonly capabilities: readonly string[];
    readonly fallbacks: Readonly<Record<string, string>>;
  }[];
  readonly recentJobs: readonly { readonly status: string }[];
}

/**
 * The provider panel's own verdict, in the same three-state vocabulary every
 * other row on this board uses.
 *
 * It had none until now, and that was what got reported: seven conditions each
 * carried a Ready badge and the provider card underneath them carried eight
 * grey chips and no verdict at all, so the one panel naming the launch delivery
 * partner was the only thing on a green page that did not look green. A panel
 * on a health board states its health.
 *
 * What it can honestly state is narrower than it looks, and the narrowness is
 * the point. There is no cheap liveness signal: the `DeliveryProvider` port has
 * no free health call, a real probe would mean a paid quote, and the credential
 * check that suggests itself is already enforced harder upstream - the Uber
 * Direct adapter trims and rejects an empty secret when it is CONSTRUCTED, so a
 * deployment missing one has no runtime at all and every internal endpoint answers
 * 500. There is no state in which this panel renders beside absent credentials.
 *
 * So the verdict is built from the two things that do vary at runtime:
 *
 * - `RED` - the adapter declares nothing it can do. Nothing can be dispatched.
 * - `AMBER` - a capability is running on a substitute (RTE-022), or a delivery
 *   dispatched today has failed. The dispatch failure rate is the honest live
 *   signal the architecture notes already point at, and the panel is holding
 *   the rows it comes from.
 * - `GREEN` - everything declared, nothing substituted, nothing failed.
 */
export function providerHealth(status: ProviderStatusLike): ProviderHealth {
  // No provider set: every new booking is refused (RTE-023).
  if (status.active === null) return 'RED';
  const capabilities = status.declarations.reduce(
    (total, declaration) => total + declaration.capabilities.length,
    0,
  );
  if (capabilities === 0) return 'RED';
  const substituted = status.declarations.some(
    (declaration) => Object.keys(declaration.fallbacks).length > 0,
  );
  const failed = status.recentJobs.some((job) => job.status === 'FAILED');
  return substituted || failed ? 'AMBER' : 'GREEN';
}
